import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '../api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import { 
  PlusIcon, 
  EyeIcon, 
  CheckIcon, 
  XMarkIcon
} from '@heroicons/react/24/outline';

const Expenses = () => {
  const { user, canAccess } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [modalType, setModalType] = useState('create'); // create, view, approve, reject
  const [approvedAmountInput, setApprovedAmountInput] = useState('');
  const [commentInput, setCommentInput] = useState('');
  const [newComment, setNewComment] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    dateFrom: '',
    dateTo: ''
  });

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();

  const typeLabel = (type) => {
    if (type === 'indent' || type === 'intend') return 'Indent';
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'N/A';
  };

  const actorLabel = (person) => {
    if (!person?.name) return 'N/A';
    return person.employeeId ? `${person.name} (${person.employeeId})` : person.name;
  };

  const latestActionBy = (expense, actions) => {
    const history = [...(expense.approvalHistory || [])]
      .reverse()
      .find((entry) => actions.includes(entry.action));
    return history?.by;
  };

  const actionLabel = (action) => {
    if (action === 'amount_modified') return 'approved with amount change';
    return action ? action.replace('_', ' ') : 'updated';
  };

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await api.get(`/api/expenses?${params}`);
      setExpenses(response.data.expenses);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const onSubmit = async (data) => {
    try {
      await api.post('/api/expenses', {
        type: data.type,
        purpose: data.purpose,
        requestedAmount: data.amount?.requested,
        description: data.description,
        date: data.date,
        client: data.client || undefined
      });

      toast.success('Expense request created successfully');
      setShowModal(false);
      reset();
      fetchExpenses();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to create expense');
    }
  };

  const handleApprove = async (expenseId, approvedAmount, comment) => {
    try {
      await api.put(`/api/expenses/${expenseId}/approve`, {
        approvedAmount,
        comment
      });
      toast.success('Expense approved successfully');
      setShowModal(false);
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to approve expense');
    }
  };

  const handleReject = async (expenseId, comment) => {
    try {
      await api.put(`/api/expenses/${expenseId}/reject`, { comment });
      toast.success('Expense rejected');
      setShowModal(false);
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to reject expense');
    }
  };

  const handlePay = async (expenseId) => {
    try {
      await api.put(`/api/expenses/${expenseId}/pay`);
      toast.success('Expense marked as paid');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to mark expense as paid');
    }
  };

  const fetchExpenseDetails = async (expenseId) => {
    try {
      const response = await api.get(`/api/expenses/${expenseId}`);
      setSelectedExpense(response.data.expense);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch expense details');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!selectedExpense || !newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    try {
      setCommentSubmitting(true);
      const response = await api.post(`/api/expenses/${selectedExpense._id}/comment`, {
        message: newComment.trim()
      });
      setSelectedExpense((current) => ({
        ...current,
        comments: [...(current?.comments || []), response.data.comment]
      }));
      setNewComment('');
      fetchExpenses();
      toast.success('Comment added');
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to add comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      pending: 'status-pending',
      approved: 'status-approved',
      rejected: 'status-rejected',
      paid: 'status-paid'
    };
    return classes[status] || 'status-pending';
  };

  const openModal = (type, expense = null) => {
    setModalType(type);
    setSelectedExpense(expense);
    setApprovedAmountInput(expense?.amount?.requested ?? '');
    setCommentInput('');
    setNewComment('');
    setShowModal(true);
    if (type === 'view' && expense?._id) {
      fetchExpenseDetails(expense._id);
    }
  };

  const submitApprove = async (e) => {
    e.preventDefault();
    if (!selectedExpense) return;
    setActionSubmitting(true);
    await handleApprove(
      selectedExpense._id,
      approvedAmountInput === '' ? undefined : Number(approvedAmountInput),
      commentInput || undefined
    );
    setActionSubmitting(false);
  };

  const submitReject = async (e) => {
    e.preventDefault();
    if (!selectedExpense) return;
    if (!commentInput.trim()) {
      toast.error('A reason is required to reject');
      return;
    }
    setActionSubmitting(true);
    await handleReject(selectedExpense._id, commentInput);
    setActionSubmitting(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-600">Manage expense requests and approvals</p>
        </div>
        <button
          onClick={() => openModal('create')}
          className="btn-primary flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Expense
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="input-field"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
          </select>
          
          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="input-field"
          >
            <option value="">All Types</option>
            <option value="conveyance">Conveyance</option>
            <option value="bill">Bill</option>
            <option value="indent">Indent</option>
          </select>
          
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            className="input-field"
            placeholder="From Date"
          />
          
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            className="input-field"
            placeholder="To Date"
          />
        </div>
      </div>

      {/* Expenses Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type & Purpose
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <div className="spinner mx-auto"></div>
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No expenses found
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center">
                          <span className="capitalize font-medium text-gray-900">
                            {typeLabel(expense.type)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-900">{expense.purpose}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {expense.description}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Requested by {actorLabel(expense.requestedBy)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(expense.amount.approved || expense.amount.requested)}
                      </div>
                      {expense.amount.approved && expense.amount.approved !== expense.amount.requested && (
                        <div className="text-xs text-gray-500">
                          Requested: {formatCurrency(expense.amount.requested)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(expense.status)}>
                        {expense.status}
                      </span>
                      {expense.referenceNumber && (
                        <div className="text-xs text-gray-500 mt-1">
                          Ref: {expense.referenceNumber}
                        </div>
                      )}
                      {latestActionBy(expense, ['approved', 'amount_modified']) && (
                        <div className="text-xs text-gray-500 mt-1">
                          Approved by {actorLabel(latestActionBy(expense, ['approved', 'amount_modified']))}
                        </div>
                      )}
                      {latestActionBy(expense, ['paid']) && (
                        <div className="text-xs text-gray-500 mt-1">
                          Paid by {actorLabel(latestActionBy(expense, ['paid']))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(expense.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => openModal('view', expense)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      
                      {canAccess('approve_expenses') && expense.status === 'pending' && (
                        <>
                          <button
                            onClick={() => openModal('approve', expense)}
                            className="text-green-600 hover:text-green-900"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openModal('reject', expense)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      
                      {user?.role === 'accounts' && expense.status === 'approved' && (
                        <button
                          onClick={() => handlePay(expense._id)}
                          className="btn-success text-xs px-2 py-1"
                        >
                          Mark Paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {modalType === 'create' && 'Create New Expense'}
                {modalType === 'view' && 'Expense Details'}
                {modalType === 'approve' && 'Approve Expense'}
                {modalType === 'reject' && 'Reject Expense'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {modalType === 'create' && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Type</label>
                    <select
                      {...register('type', { required: 'Type is required' })}
                      className="input-field"
                    >
                      <option value="">Select Type</option>
                      <option value="conveyance">Conveyance</option>
                      <option value="bill">Bill</option>
                      <option value="indent">Indent</option>
                    </select>
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Amount</label>
                    <input
                      {...register('amount.requested', { 
                        required: 'Amount is required',
                        min: { value: 0.01, message: 'Amount must be greater than 0' }
                      })}
                      type="number"
                      step="0.01"
                      className="input-field"
                    />
                    {errors.amount?.requested && (
                      <p className="mt-1 text-sm text-red-600">{errors.amount.requested.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="label">Purpose</label>
                  <input
                    {...register('purpose', { required: 'Purpose is required' })}
                    type="text"
                    className="input-field"
                  />
                  {errors.purpose && (
                    <p className="mt-1 text-sm text-red-600">{errors.purpose.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">Description</label>
                  <textarea
                    {...register('description', { required: 'Description is required' })}
                    rows={3}
                    className="input-field"
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">Date</label>
                  <input
                    {...register('date', { required: 'Date is required' })}
                    type="date"
                    className="input-field"
                  />
                  {errors.date && (
                    <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary"
                  >
                    {isSubmitting ? 'Creating...' : 'Create Expense'}
                  </button>
                </div>
              </form>
            )}

            {modalType === 'view' && selectedExpense && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-500">Type:</span>
                    <span className="ml-2">{typeLabel(selectedExpense.type)}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Status:</span>
                    <span className={`ml-2 ${getStatusBadge(selectedExpense.status)}`}>
                      {selectedExpense.status}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Amount:</span>
                    <span className="ml-2">
                      {formatCurrency(selectedExpense.amount.approved || selectedExpense.amount.requested)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Date:</span>
                    <span className="ml-2">{new Date(selectedExpense.date).toLocaleDateString()}</span>
                  </div>
                </div>
                
                <div>
                  <span className="font-medium text-gray-500">Purpose:</span>
                  <p className="mt-1">{selectedExpense.purpose}</p>
                </div>
                
                <div>
                  <span className="font-medium text-gray-500">Description:</span>
                  <p className="mt-1">{selectedExpense.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-500">Requested by:</span>
                    <span className="ml-2">{actorLabel(selectedExpense.requestedBy)}</span>
                  </div>
                  {selectedExpense.referenceNumber && (
                    <div>
                      <span className="font-medium text-gray-500">Payment ref:</span>
                      <span className="ml-2">{selectedExpense.referenceNumber}</span>
                    </div>
                  )}
                </div>

                {selectedExpense.approvalHistory.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-500">History:</span>
                    <div className="mt-2 space-y-2">
                      {selectedExpense.approvalHistory.map((history, index) => (
                        <div key={index} className="text-sm bg-gray-50 p-2 rounded">
                          <span className="font-medium capitalize">{actionLabel(history.action)}</span>
                          <span className="text-gray-600"> by {actorLabel(history.by)}</span>
                          {history.comment && <p className="text-gray-600">{history.comment}</p>}
                          {history.newAmount && history.previousAmount !== history.newAmount && (
                            <p className="text-xs text-gray-500">
                              Amount changed from {formatCurrency(history.previousAmount)} to {formatCurrency(history.newAmount)}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            {new Date(history.timestamp).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <span className="font-medium text-gray-500">Comments / Audit Context:</span>
                  <div className="mt-2 space-y-2">
                    {(selectedExpense.comments || []).length === 0 ? (
                      <p className="text-sm text-gray-500 bg-gray-50 rounded p-3">
                        No comments yet. Add context for approvals, payment questions, or receipt follow-up.
                      </p>
                    ) : (
                      [...selectedExpense.comments]
                        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                        .map((comment) => (
                          <div key={comment._id || `${comment.by?._id}-${comment.timestamp}`} className="text-sm bg-gray-50 p-3 rounded">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                              <span className="font-medium text-gray-900">{actorLabel(comment.by)}</span>
                              <span className="text-xs text-gray-500">{new Date(comment.timestamp).toLocaleString()}</span>
                            </div>
                            <p className="mt-1 text-gray-700 whitespace-pre-wrap">{comment.message}</p>
                          </div>
                        ))
                    )}
                  </div>

                  <form onSubmit={handleAddComment} className="mt-3 space-y-2">
                    <label className="label">Add Comment</label>
                    <textarea
                      rows={3}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      className="input-field"
                      placeholder="Add approval context, payment note, or follow-up question"
                    />
                    <div className="flex justify-end">
                      <button type="submit" disabled={commentSubmitting} className="btn-secondary">
                        {commentSubmitting ? 'Adding...' : 'Add Comment'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {modalType === 'approve' && selectedExpense && (
              <form onSubmit={submitApprove} className="space-y-4">
                <div className="bg-gray-50 rounded p-3 text-sm">
                  <div><span className="font-medium text-gray-500">Requested by:</span> {actorLabel(selectedExpense.requestedBy)}</div>
                  <div><span className="font-medium text-gray-500">Purpose:</span> {selectedExpense.purpose}</div>
                  <div><span className="font-medium text-gray-500">Requested amount:</span> {formatCurrency(selectedExpense.amount.requested)}</div>
                </div>

                <div>
                  <label className="label">Approved Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={approvedAmountInput}
                    onChange={(e) => setApprovedAmountInput(e.target.value)}
                    className="input-field"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave as requested to approve in full, or change to adjust the amount.
                  </p>
                </div>

                <div>
                  <label className="label">Comment (optional)</label>
                  <textarea
                    rows={3}
                    value={commentInput}
                    onChange={(e) => setCommentInput(e.target.value)}
                    className="input-field"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" disabled={actionSubmitting} className="btn-success">
                    {actionSubmitting ? 'Approving...' : 'Approve'}
                  </button>
                </div>
              </form>
            )}

            {modalType === 'reject' && selectedExpense && (
              <form onSubmit={submitReject} className="space-y-4">
                <div className="bg-gray-50 rounded p-3 text-sm">
                  <div><span className="font-medium text-gray-500">Requested by:</span> {actorLabel(selectedExpense.requestedBy)}</div>
                  <div><span className="font-medium text-gray-500">Purpose:</span> {selectedExpense.purpose}</div>
                  <div><span className="font-medium text-gray-500">Amount:</span> {formatCurrency(selectedExpense.amount.requested)}</div>
                </div>

                <div>
                  <label className="label">Reason for rejection <span className="text-red-600">*</span></label>
                  <textarea
                    rows={3}
                    value={commentInput}
                    onChange={(e) => setCommentInput(e.target.value)}
                    className="input-field"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" disabled={actionSubmitting} className="btn-danger">
                    {actionSubmitting ? 'Rejecting...' : 'Reject'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
